import React from 'react';
import type { LucideIcon } from 'lucide-react';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputType = 'text' | 'email' | 'url' | 'number' | 'tel' | 'search' | 'password';

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  size?: InputSize;
  type?: InputType;
  error?: string;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
}

const SIZE: Record<InputSize, { input: string; icon: number }> = {
  sm: { input: 'h-8 text-xs', icon: 14 },
  md: { input: 'h-10 text-sm', icon: 16 },
  lg: { input: 'h-12 text-md', icon: 18 },
};

const Input: React.FC<InputProps> = ({
  size = 'md',
  type = 'text',
  error,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  className = '',
  id,
  ...rest
}) => {
  const sz = SIZE[size];
  const errorId = error ? `${id}-error` : undefined;
  const padX = LeftIcon ? 'pl-9' : 'pl-3';
  const padR = RightIcon ? 'pr-9' : 'pr-3';
  const borderColor = error ? 'border-error' : 'border-default';
  return (
    <div className="w-full">
      <div className="relative">
        {LeftIcon && (
          <LeftIcon
            size={sz.icon}
            strokeWidth={1.75}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            aria-hidden
          />
        )}
        <input
          id={id}
          type={type}
          aria-invalid={!!error}
          aria-describedby={errorId}
          className={`w-full bg-input ${borderColor} border rounded-md ${padX} ${padR} ${sz.input} text-pt-forest placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-pt-gold focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-shadow duration-[var(--motion-duration-fast)] ${className}`.trim()}
          {...rest}
        />
        {RightIcon && (
          <RightIcon
            size={sz.icon}
            strokeWidth={1.75}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            aria-hidden
          />
        )}
      </div>
      {error && (
        <p id={errorId} className="text-xs text-error mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

export default Input;
